

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import * as Sentry from "@sentry/react";
import { AuthError } from "../errors/sentryErrors";
import { API_URL, FUNNEL_URL } from "../config";
import { isBannedUser, USER_BANNED_EVENT } from "../utils/roles";

const AuthContext = createContext(null);

const TOKEN_KEY = 'romaya_auth_token';

export function getAuthToken() {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function setAuthToken(token) {
  try {
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
    } else {
      localStorage.removeItem(TOKEN_KEY);
    }
  } catch {
  }
}

function clearAuthToken() {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Set when any call answers USER_BANNED, even if the user object does not say so yet.
  const [bannedSeen, setBannedSeen] = useState(false);

  useEffect(() => {
    const onBanned = () => setBannedSeen(true);
    window.addEventListener(USER_BANNED_EVENT, onBanned);
    return () => window.removeEventListener(USER_BANNED_EVENT, onBanned);
  }, []);

  const fetchWithRetry = async (url, options, retries = 2, timeout = 5000) => {
    for (let i = 0; i <= retries; i++) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);

        const response = await fetch(url, {
          ...options,
          signal: controller.signal,
        });

        clearTimeout(timeoutId);
        return response;
      } catch (err) {
        if (i === retries) throw err;
        await new Promise(resolve => setTimeout(resolve, 500 * (i + 1)));
      }
    }
  };

  const checkSession = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const existingToken = getAuthToken();
      if (existingToken) {
        try {
          const tokenResponse = await fetchWithRetry(`${API_URL}/api/auth/validate`, {
            method: 'GET',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${existingToken}`,
            },
          }, 1, 3000);

          if (tokenResponse.ok) {
            const tokenData = await tokenResponse.json();
            if (tokenData.valid && tokenData.user) {
              try {
                const funnelCheck = await fetchWithRetry(`${FUNNEL_URL}/api/auth/cross-session`, {
                  method: 'GET',
                  credentials: 'include',
                  headers: { 'Content-Type': 'application/json' },
                }, 1, 3000);
                if (funnelCheck.ok) {
                  const funnelData = await funnelCheck.json();
                  if (!funnelData.authenticated) {
                    clearAuthToken();
                    setUser(null);
                    setLoading(false);
                    return null;
                  }
                }
              } catch {
              }
              setUser(tokenData.user);
              setLoading(false);
              return tokenData.user;
            }
          }
        } catch (tokenErr) {
          Sentry.withScope((scope) => {
            scope.setTag("feature", "auth");
            scope.setTag("app", "web");
            scope.setTag("error_source", tokenErr instanceof TypeError ? "network" : "server");
            Sentry.captureException(new AuthError(tokenErr instanceof Error ? tokenErr.message : String(tokenErr)));
          });
          console.warn('[AuthContext] Token validation failed, falling back to funnel session', tokenErr);
        }
      }

      const funnelResponse = await fetchWithRetry(`${FUNNEL_URL}/api/auth/cross-session`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      }, 2, 5000);

      if (funnelResponse.ok) {
        const funnelData = await funnelResponse.json();

        if (funnelData.authenticated && funnelData.user) {
          try {
            const syncResponse = await fetchWithRetry(`${API_URL}/api/auth/sync`, {
              method: 'POST',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ user: funnelData.user }),
            }, 1, 5000);

            if (syncResponse.ok) {
              const syncData = await syncResponse.json();
              if (syncData.token) {
                setAuthToken(syncData.token);
              }
              setUser(syncData.user || funnelData.user);
              return syncData.user || funnelData.user;
            } else {
              setUser(funnelData.user);
              return funnelData.user;
            }
          } catch (syncErr) {
            Sentry.withScope((scope) => {
              scope.setTag("feature", "auth");
              scope.setTag("app", "web");
              scope.setTag("error_source", syncErr instanceof TypeError ? "network" : "server");
              Sentry.captureException(new AuthError(syncErr instanceof Error ? syncErr.message : String(syncErr)));
            });
            console.warn('[AuthContext] User sync failed, using funnel user as-is', syncErr);
            setUser(funnelData.user);
            return funnelData.user;
          }
        }
      }

      clearAuthToken();
      setUser(null);
      return null;
    } catch (err) {
      Sentry.withScope((scope) => {
        scope.setTag("feature", "auth");
        scope.setTag("app", "web");
        scope.setTag("error_source", err instanceof TypeError ? "network" : "server");
        Sentry.captureException(new AuthError(err instanceof Error ? err.message : String(err)));
      });
      const existingToken = getAuthToken();
      if (!existingToken) {
        setUser(null);
      }
      setError('Unable to verify authentication. Please refresh the page.');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) {
      Sentry.setUser({ id: user.id, email: user.email });
    } else {
      Sentry.setUser(null);
    }
  }, [user]);

  useEffect(() => {
    checkSession();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkSession();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [checkSession]);

  const logout = useCallback(async () => {
    Sentry.addBreadcrumb({
      category: "user-action",
      message: "User logged out",
      level: "info",
    });
    clearAuthToken();
    // Drop the in-memory session in the same tick as the token. Without this
    // `isAuthenticated` stays true while the token is already gone, so every
    // mounted screen still believes it is signed in and keeps fetching — and the
    // API answers those with 401 "Invalid or expired session". The dashboard
    // rendered that as an error state mid-logout. The await below is a full
    // network round-trip, so the window this closes is not a theoretical one.
    setUser(null);

    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (err) {
      Sentry.withScope((scope) => {
        scope.setTag("feature", "auth");
        scope.setTag("app", "web");
        scope.setTag("error_source", err instanceof TypeError ? "network" : "server");
        Sentry.captureException(new AuthError(err instanceof Error ? err.message : String(err)));
      });
      console.warn('[AuthContext] Logout API call failed; proceeding with client-side logout', err);
    }

    const romayaUrl = window.location.origin;
    window.location.href = `${FUNNEL_URL}/auth/signout?callbackUrl=${encodeURIComponent(romayaUrl)}`;
  }, []);

  // Returns the resolved user (or null) so callers -- specifically
  // AuthModalContext's handleAuthenticated -- can tell whether sign-in
  // actually succeeded instead of assuming it did just because this
  // resolved without throwing.
  const refreshSession = useCallback(async () => {
    return checkSession();
  }, [checkSession]);

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    isBanned: !!user && (isBannedUser(user) || bannedSeen),
    logout,
    refreshSession,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
